import React, { useState, useEffect } from 'react'
import ValidationBanner from '../../../components/common/ValidationBanner'
import ButtonGroup from './ButtonGroup'
import HoldValidationMessage from './HoldValidationMessage'
import { canHoldRequest } from './holdValidation'
import { APPLY_REQUEST_STATUS_HEADING } from './radioOptions'
import { ButtonStateConfig } from './buttonStateUtils'
import StatusRadioOptions from './StatusRadioOptions'
import { isISODateInPast } from '../../../utils/date'

interface ReturnedTimeOffFormProps {
  onSubmit?: (data: { status: string; comments: string }) => void
  onCancel?: () => void
  onStatusChange?: (status: string) => void
  isRetracted?: boolean
  numberOfDates?: number
  dateRequested?: string
  activeForm?: 'approved' | 'denied' | 'hold' | 'partiallyapproved' | 'returned'
  isDisabledDueToDate?: boolean
  buttonStateConfig?: ButtonStateConfig
  toTypeCd?: string
  disableEndDate?: string
}

const ReturnedTimeOffForm: React.FC<ReturnedTimeOffFormProps> = ({
  onSubmit,
  onCancel,
  onStatusChange,
  isRetracted = false,
  numberOfDates = 1,
  dateRequested = '',
  activeForm = 'approved',
  isDisabledDueToDate = false,
  buttonStateConfig,
  toTypeCd,
  disableEndDate,
}) => {
  const [approverComments, setApproverComments] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('returned')
  const [validationError, setValidationError] = useState<string | null>(null)

  // Calculate if Hold can be applied based on dateRequested
  const canHold = canHoldRequest(dateRequested, toTypeCd)

  // Sync internal state with parent's activeForm when it changes
  useEffect(() => {
    if (activeForm === 'returned') {
      setSelectedStatus('returned')
    }
  }, [activeForm])

  const handleStatusChange = (status: string) => {
    setSelectedStatus(status)
    // Clear validation error when changing status
    setValidationError(null)
    if (onStatusChange) {
      onStatusChange(status)
    }
  }
  const isPastDate = (date?: string) => {
    return isISODateInPast(date)
  }
  // Disable when BOTH start and end dates are before today
  const areBothDatesInPast =
    isPastDate(dateRequested) && isPastDate(disableEndDate)
  const handleSubmit = () => {
    // Validate that comments are provided when status is 'returned'
    if (selectedStatus === 'returned' && !approverComments.trim()) {
      setValidationError('Returned comments are required')
      return
    }

    // Clear validation error if all checks pass
    setValidationError(null)

    if (onSubmit) {
      onSubmit({
        status: selectedStatus,
        comments: approverComments,
      })
    }
  }

  return (
    <div className="contents">
      {validationError && (
        <div className="mb-6 px-[24px] pt-[24px] col-span-2">
          <ValidationBanner
            variant="error"
            heading={
              <div>
                <div className="!pb-0 !p1 -medium [font-size:20px]">
                  Returned comments are required.
                </div>
              </div>
            }
          />
        </div>
      )}

      <div className="!px-[24px] !pt-0 !pb-[24px]">
        <p className="p4 -medium text-[#0D1C3D] !m-0 !pb-1">
          {APPLY_REQUEST_STATUS_HEADING.text}
        </p>

        {/* Status Radio Buttons */}
        <div className="space-y-4 mb-8 !pb-6">
          <StatusRadioOptions
            selectedStatus={selectedStatus}
            onStatusChange={handleStatusChange}
            buttonStateConfig={buttonStateConfig}
            canHold={canHold}
            isRetracted={isRetracted}
            numberOfDates={numberOfDates}
            disableAllOptions={isDisabledDueToDate || areBothDatesInPast}
          />
        </div>

        {/* Hold Validation Message */}
        {!canHold && (
          <HoldValidationMessage
            message={
              toTypeCd === 'A'
                ? 'Cannot Hold Request: Start date is less than 1 day'
                : 'Cannot Hold Request: Start date is less than 15 days'
            }
          />
        )}

        {/* Approver Comments */}
        <div className="mb-6 pt-6 !pb-6" id="returned-comments">
          <label htmlFor="comments" className="block p3 -medium mb-1">
            Approver Comments
            {selectedStatus === 'returned' && (
              <span className="text-red-600">*</span>
            )}
          </label>
          <textarea
            id="comments"
            className="w-full border rounded p-2 min-h-20 disabled:bg-gray-100 disabled:cursor-not-allowed"
            maxLength={1000}
            value={approverComments}
            onChange={(e) => {
              setApproverComments(e.target.value)
              setValidationError(null)
            }}
            disabled={isDisabledDueToDate || areBothDatesInPast}
          />
          <div className="flex justify-between items-start mt-1">
            {selectedStatus === 'returned' && validationError ? (
              <p className="text-red-600 text-xs flex items-center gap-1">
                <img
                  src="/assets/images/errorCircleSmall.svg"
                  alt="error"
                  className="inline w-4 h-4"
                />
                <span>Required</span>
              </p>
            ) : (
              <div className="text-left text-xs text-gray-500">
                {1000 - approverComments.length} of 1000 characters left
              </div>
            )}
          </div>
        </div>

        {/* Buttons */}
        <ButtonGroup
          onCancel={onCancel}
          onSubmit={handleSubmit}
          submitDisabled={isDisabledDueToDate || areBothDatesInPast}
        />
      </div>
    </div>
  )
}

export default ReturnedTimeOffForm
